import {
  type AcademicQualificationFormData,
  DEGREE_LEVEL,
} from '../../../graphql';
import {
  type FormController,
  FormModificationMode,
} from '../../../../../utils';

export type AcademicQualificationControl = Record<
  keyof AcademicQualificationFormData,
  boolean
> & {
  enableSave: boolean;
  enableDelete: boolean;
  enableSaveCondition: boolean;
};

export type AcademicQualificationFieldPermissions = Omit<
  AcademicQualificationControl,
  'academicQualificationId'
>;

const CHN = 'CHN';
export const editAcademicQualifyPermission = (
  initialValues: AcademicQualificationFormData,
  formikValues: AcademicQualificationFormData,
  enableEditCondition: FormController,
): AcademicQualificationFieldPermissions => {
  const { formModificationMode } = enableEditCondition;
  // Academic qualification form field permissions
  const disableAcademicQualificationFieldPermissions: AcademicQualificationFieldPermissions =
    {
      presentIndicator: false,
      degreeLevel: false,
      programmeName: false,
      from: false,
      to: false,
      studyMode: false,
      currentStudyYear: false,
      institutionName: false,
      institutionCountry: false,
      instructionMedium: false,
      otherInstructionMedium: false,
      studyField: false,
      qualificationMeans: false,
      qualificationStatus: false,
      awardDate: false,
      awardClassification: false,
      officialMark: false,
      outOfGpa: false,
      marks: false,
      markConversion: false,
      convertGPARuleId: false,
      letterToGPARule: false,
      digitToLetterRule: false,
      convertedMark: false,
      expectUpdateDate: false,
      withdrawDate: false,
      academicOnlineVerificationCode: false,
      degreeOnlineVerificationCode: false,
      officialClassRanking: false,
      outOfRank: false,
      certificates: false,
      transcripts: false,
      markExplanations: false,
      classRankProofs: false,
      otherDocuments: false,
      gpaIndicator: false,
      rankIndicator: false,
      academicCredentialIndicator: false,
      degreeCertIndicator: false,
      enableSave: false,
      enableDelete: false,
      enableSaveCondition: false,
    };

  const enableAcademicQualificationFieldPermissions: AcademicQualificationFieldPermissions =
    {
      ...Object.assign(
        {},
        ...Object.keys(disableAcademicQualificationFieldPermissions).map(
          key => ({
            [key]: true,
          }),
        ),
      ),
      awardClassification: formikValues.institutionCountry !== CHN,
      qualificationMeans:
        formikValues.degreeLevel !== DEGREE_LEVEL.BACHELOR_DEGREE,
      officialClassRanking: !formikValues.rankIndicator,
      outOfRank: !formikValues.rankIndicator,
      officialMark: !formikValues.gpaIndicator,
      outOfGpa: !formikValues.gpaIndicator,
      convertedMark: !formikValues.gpaIndicator,
      enableSaveCondition:
        formModificationMode === FormModificationMode.CONDITION_MEET,
    };

  // TODO: keep currently config and will update in the future
  const modifyAcademicQualificationFieldPermissions: AcademicQualificationFieldPermissions =
    {
      ...disableAcademicQualificationFieldPermissions,
      ...(initialValues.presentIndicator && {
        // depends on the data that saved last time
        presentIndicator: true,
        to: true,
        currentStudyYear: true,
        qualificationStatus: true,
        expectUpdateDate: true,
        withdrawDate: true,
        awardDate: true,
        awardClassification: true,
        academicCredentialIndicator: true,
        degreeCertIndicator: true,
        academicOnlineVerificationCode: true,
        degreeOnlineVerificationCode: true,
        officialClassRanking: !formikValues.rankIndicator,
        outOfRank: !formikValues.rankIndicator,
        officialMark: !formikValues.gpaIndicator,
        outOfGpa: !formikValues.gpaIndicator,
        convertedMark: !formikValues.gpaIndicator,
        certificates: true,
        transcripts: true,
        markExplanations: true,
        classRankProofs: true,
        otherDocuments: true,
        gpaIndicator: true,
        rankIndicator: true,
        enableSave: true,
      }),
    };

  const academicQualificationPermission: Record<
    FormModificationMode,
    AcademicQualificationFieldPermissions
  > = {
    [FormModificationMode.ENABLE]: enableAcademicQualificationFieldPermissions,
    [FormModificationMode.DISABLE]:
      disableAcademicQualificationFieldPermissions,
    [FormModificationMode.MODIFIED_ONLY]:
      modifyAcademicQualificationFieldPermissions,
    [FormModificationMode.CONDITION_MEET]: {
      ...enableAcademicQualificationFieldPermissions,
      // In meet condition situation, not allow the applicant to delete
      // only allow them to revise/add
      enableDelete: false,
    },
  };

  return academicQualificationPermission[formModificationMode];
};
